<template>
  <vue-final-modal
    classes="flex justify-center items-center"
    v-model="isShowModal"
  >
    <div
      class="bg-white px-6 py-4 w-full h-full overflow-auto flex flex-col align-items-center rounded"
    >
      <div class="text-lg mb-5">Import Site</div>
      <div
        @dragover.prevent="dragStarted"
        @dragleave.prevent="dragLeave"
        @drop.prevent="drop"
        class="drag-image flex flex-col justify-content-center align-items-center rounded"
        :class="isShowDragImage ? 'drag-over' : ''"
      >
        <div v-if="!selectedFile" class="flex flex-col justify-content-center align-items-center">
          <div v-if="!isShowDragImage" class="text-base">
            Drag & Drop File Here
          </div>
          <div v-else class="text-base">Release to Select File</div>
          <div class="text-sm">OR</div>
          <button
            @click="openFileUploadPopup"
            class="btn btn-default btn-bla ck"
          >
            Browse File
          </button>
        </div>
        <div v-else class="flex flex-col justify-content-center align-items-center">
          <i class="fa fa-file text-8xl text-gray-400" aria-hidden="true"></i>
          <div>{{ this.selectedFile.name }}</div>
        </div>
        <input
          type="file"
          id="import-file-input"
          @change="uploadedThroughButton"
          hidden
          accept=".json"
        />
      </div>
      <small class="mt-1 text-gray-400"
        >Please upload a valid JSON file. Only files with a .json extension are
        accepted.</small
      >
      <a
        @click="handleImportClick"
        href="#"
        class="btn btn-default btn-bla ck mt-5"
      >
        <div
          v-if="loading"
          class="spinner-border"
          style="width: 1rem; height: 1rem"
          role="status"
        ></div>
        <div v-else>Import</div>
      </a>
    </div>
    <VersionSelectionModal 
      :isVisible="showVersionModal" 
      :versions="availableVersions" 
      modalTitle="Select Versions to Import"
      actionText="Import"
      @confirm="handleVersionSelection" 
      @cancel="handleVersionCancel" 
    />
  </vue-final-modal>
</template>
<script>
import * as siteService from "../service/site";
import VersionSelectionModal from "./VersionSelectionModal.vue";

export default {
  name: "SingleImageDrag",
  components: {
    VersionSelectionModal,
  },
  data: () => ({
    isShowModal: true,
    isShowDragImage: false,
    selectedFile: null,
    loading: false,
    isFetchData: false,
    showVersionModal: false,
    availableVersions: [],
    parsedJsonData: null,
    selectedVersions: [],
  }),

  watch: {
    isShowModal() {
      this.$emit("onCloseImportModal", this.isFetchData);
    },
  },
  methods: {
    dragStarted() {
      console.log("drag entered");
      this.isShowDragImage = true;
    },
    dragLeave() {
      this.isShowDragImage = false;
    },
    drop(event) {
      const dropArea = document.querySelector(".drag-image");
      let file = event.dataTransfer.files[0];
      this.selectedFile = file;
      dropArea.classList.add("active");
      this.viewFile(file);
    },
    uploadedThroughButton(event) {
      this.selectedFile = event.target.files[0];
      console.log("input", this.selectedFile, event);
      this.viewFile(this.selectedFile);
    },
    viewFile(file) {
      const dropArea = document.querySelector(".drag-image"),
        dragText = dropArea.querySelector("h6");
      let fileType = file.type;
      let validExtensions = ["application/json"];
      if (!validExtensions.includes(fileType)) {
        alert("This is not an JSON File!");
        dropArea.classList.remove("active");
        dragText.textContent = "Drag & Drop to Upload File";
      }
    },
    openFileUploadPopup() {
      document.querySelector("#import-file-input").click();
    },

    async handleImportClick() {
      if (!this.selectedFile) {
        this.$notify({
          type: "warn",
          text: "Please select a file to import.",
        });
        return;
      }

      try {
        // Parse JSON file
        const jsonData = await this.parseJsonFile(this.selectedFile);
        
        // Validate JSON structure (handles both old and new format)
        this.validateJsonStructure(jsonData);
        
        // Extract versions from both formats (sites array or single site)
        const versions = this.extractVersions(jsonData);
        
        if (!versions || versions.length === 0) {
          // No versions found - import directly without version selection
          this.parsedJsonData = jsonData;
          this.selectedVersions = null; // null indicates no version selection
          await this.uploadFileToServer();
        } else {
          // Versions found - show version modal for user to select
          this.parsedJsonData = jsonData;
          this.availableVersions = versions;
          this.showVersionModal = true;
        }
      } catch (err) {
        console.log("err: while parsing JSON", err);
        this.$notify({
          type: "error",
          text: err.message || "Invalid JSON file. Please check the file format.",
        });
      }
    },


    async parseJsonFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const jsonData = JSON.parse(e.target.result);
            resolve(jsonData);
          } catch (parseError) {
            reject(new Error("Failed to parse JSON file. Invalid JSON format."));
          }
        };
        reader.onerror = () => {
          reject(new Error("Failed to read file."));
        };
        reader.readAsText(file);
      });
    },

    validateJsonStructure(jsonData) {
      if (!jsonData || typeof jsonData !== 'object') {
        throw new Error("Invalid JSON structure. Expected an object.");
      }
      
      // Check for new format (sites array) or old format (single site object)
      const hasSitesArray = jsonData.sites && Array.isArray(jsonData.sites);
      const hasSingleSite = jsonData.site && typeof jsonData.site === 'object';
      
      if (!hasSitesArray && !hasSingleSite) {
        throw new Error("Invalid JSON structure. Missing 'sites' array or 'site' object.");
      }
      
      if (hasSitesArray && jsonData.sites.length === 0) {
        throw new Error("No site data found in the JSON file.");
      }
    },
    isNewFormat(jsonData) {
      // New format has 'sites' array, old format has 'site' object
      return jsonData.sites && Array.isArray(jsonData.sites);
    },
    

    extractVersions(jsonData) {
      const versionsMap = new Map();
      const currentVersion = jsonData.siteversion?.current_version;
      const publishedVersion = jsonData.siteversion?.published_version;

      // Check for new format: sites array
      if (jsonData.sites && Array.isArray(jsonData.sites)) {
        // Extract unique versions from sites array (only versions with actual site data)
        jsonData.sites.forEach((site) => {
          if (site.version && !versionsMap.has(site.version)) {
            versionsMap.set(site.version, {
              version: site.version,
              is_current: site.version === currentVersion,
              is_published: site.version === publishedVersion,
            });
          }
        });
      }
      
      // Check for old format: single site object
      if (jsonData.site && typeof jsonData.site === 'object') {
        // Only extract version from site.version (actual site data exists)
        // Do NOT extract from siteversion.current_version or siteversion.published_version
        // as those are just metadata and don't guarantee site data exists
        if (jsonData.site.version) {
          versionsMap.set(jsonData.site.version, {
            version: jsonData.site.version,
            is_current: jsonData.site.version === currentVersion,
            is_published: jsonData.site.version === publishedVersion,
          });
        }
      }

      return Array.from(versionsMap.values());
    },


    async handleVersionSelection(selectedVersions) {
      if (!selectedVersions || selectedVersions.length === 0) {
        this.$notify({
          type: "warn",
          text: "Please select at least one version to import.",
        });
        return;
      }

      this.showVersionModal = false;
      this.selectedVersions = selectedVersions;
      await this.uploadFileToServer();
    },

    handleVersionCancel() {
      this.showVersionModal = false;
      // Keep the file, allow retry
    },


    async uploadFileToServer(newUri = null) {
      if (!this.selectedFile) {
        return;
      }

      const formData = new FormData();
      formData.append("file", this.selectedFile);
      
      // Send newUri if provided (for renaming)
      if (newUri) {
        formData.append("newUri", newUri);
      }
      
      // Only send versions if it's new format (selectedVersions is array)
      // Old format: selectedVersions will be null, so skip versions
      if (this.selectedVersions && Array.isArray(this.selectedVersions) && this.selectedVersions.length > 0) {
        // Send versions as multiple FormData entries (creates array on backend)
        this.selectedVersions.forEach(version => {
          formData.append("versions", version);
        });
      }
      
      this.loading = true;
      try {
        const response = await siteService.importSite(formData);
        
        // Check if backend returned success: false
        if (response && response.success === false) {
          // Check if error is about URI already existing
          if (response.error && response.error.includes("Site already exists with same URI")) {
            await this.handleUriConflict();
          } else {
            this.$notify({
              type: "error",
              text: response.error || "Error while importing site.",
            });
          }
          this.loading = false;
          return;
        }
        
        // Success case
        this.isFetchData = true;
        this.isShowModal = false;
        this.$notify("Site Imported Successfully.");
        // Reset state
        this.resetImportState();
      } catch (err) {
        console.log("err: while importing", err);
        if (this.isUriConflictError(err)) {
          await this.handleUriConflict();
        } else {
          this.$notify({
            type: "error",
            text: err.error || err.message || "Error while importing site.",
          });
        }
      }
      this.loading = false;
    },
    isUriConflictError(err) {
      const message = err?.error || err?.message || "";
      return message.includes("Site already exists with same URI");
    },


    async handleUriConflict() {
      // Show alert asking if user wants to rename
      const wantsToRename = window.confirm(
        "Site already exists with same URI. Do you want to rename it?"
      );
      
      if (!wantsToRename) {
        // User chose not to rename - keep file selected for retry
        return;
      }
      
      // Get new URI from user
      let newUri = window.prompt("Enter new URI:");
      
      // Validate input
      if (!newUri || !newUri.trim()) {
        this.$notify({
          type: "warn",
          text: "URI cannot be empty. Please try again.",
        });
        return;
      }
      
      newUri = newUri.trim();
      
      // Automatically retry import with new URI (backend will update it)
      await this.uploadFileToServer(newUri);
    },
    resetImportState() {
      this.selectedFile = null;
      this.parsedJsonData = null;
      this.availableVersions = [];
      this.selectedVersions = [];
      this.showVersionModal = false;
    },
  },
};
</script>

<style>
.drag-image {
  border: 1px dashed grey;
  height: 250px;
  width: 350px;
  font-weight: 400;
}
.drag-image.active {
  border: 2px solid grey;
}

.drag-image.drag-over {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  border: 3px dashed #00aeef;
}
</style>
